/**
 * @fileoverview `VenueHero` — top-of-page hero for `/venue/[slug]`.
 *
 * Mirrors the `vn-hero` block in `docs/design-system/1.5.0/templates/city/venue.html`:
 *   - Eyebrow strip (`VENUE · NEIGHBORHOOD · SINCE YYYY · <CAP>-CAP <TYPE>`)
 *   - Display H1 with skyline-glow `<em>` on the middle word (3+ words)
 *   - 50ch sub-paragraph
 *   - 4:3 cover slot — `VenueCoverPanel`, the seed-fact placard (FRE-1323)
 *
 * Two-column vs stacked is a **container** query, not a viewport one
 * (WEB-921). This hero sits in the venue-detail left column beside a 320px
 * Find-it rail from `min-[980px]`, so a viewport-`980px` two-column grid
 * turns on while the hero box is still ~550–750px. `fr` tracks default to
 * `min-width: auto` (min-content), the H1 is `clamp(56px, 7vw, 96px)`, and
 * the cover paints after the title — the last glyphs slide under the card.
 *
 * Theme-aware via `--clt-*` and `--loci-color-*` CSS variables emitted by
 * `ThemeProvider`, so it renders correctly under both `data-theme="dark"` and
 * `data-theme="light"`.
 *
 * @since WEB-163
 */
'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import { VenueCoverPanel } from '@/components/molecules/VenueCoverPanel';
import type { VenueCoordinates } from '@/types/venueDetail';

const SEPARATOR = ' · ';

export interface VenueHeroViewProps {
  /** Venue display name; rendered as the h1. */
  name: string;
  /** Neighborhood slug (kebab-case). Reserved for future use; not rendered. */
  neighborhoodSlug?: string | null;
  /** Neighborhood display name. Uppercased for the eyebrow. */
  neighborhoodName?: string | null;
  /** Year the venue opened; drives the "SINCE YYYY" eyebrow segment. */
  foundedYear?: number | null;
  /** Headline capacity (whole number); drives the "<CAP>-CAP" eyebrow segment. */
  capacity?: number | null;
  /** Capacity descriptor (e.g. "Listening room"); uppercased after capacity. */
  capacityNote?: string | null;
  /** Editorial blurb; first sentence renders as the sub-paragraph. */
  editorialBlurb?: string | null;
  /** Postal code for the cover placard's `NEIGHBORHOOD · POSTCODE` line. */
  postalCode?: string | null;
  /** Venue kind (e.g. "Listening room") for the cover placard. */
  kind?: string | null;
  /** Venue coordinates; drives the placard's degree line. Null omits it. */
  coordinates?: VenueCoordinates | null;
  /** Owner/admin-uploaded cover. Present ⇒ the placard is replaced by the photo. */
  coverUrl?: string | null;
  /** Alt text for a real cover. */
  coverAlt?: string | null;
  className?: string;
}

export interface VenueHeroProps extends VenueHeroViewProps {}

function composeEyebrow({
  neighborhoodName,
  foundedYear,
  capacity,
  capacityNote,
}: Pick<
  VenueHeroViewProps,
  'neighborhoodName' | 'foundedYear' | 'capacity' | 'capacityNote'
>): string {
  const segments: string[] = ['Venue'];

  if (neighborhoodName && neighborhoodName.trim().length > 0) {
    segments.push(neighborhoodName.trim());
  }

  if (typeof foundedYear === 'number' && Number.isFinite(foundedYear)) {
    segments.push(`Since ${foundedYear}`);
  }

  const capacitySegment = composeCapacitySegment(capacity, capacityNote);
  if (capacitySegment) {
    segments.push(capacitySegment);
  }

  return segments.join(SEPARATOR);
}

function sentenceCaseSegment(value: string): string {
  const lower = value.toLocaleLowerCase('en-US');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function composeCapacitySegment(
  capacity: VenueHeroViewProps['capacity'],
  capacityNote: VenueHeroViewProps['capacityNote'],
): string | null {
  const hasCap = typeof capacity === 'number' && Number.isFinite(capacity) && capacity > 0;
  const note = capacityNote && capacityNote.trim().length > 0 ? capacityNote.trim() : null;

  if (hasCap && note && note.includes(String(capacity))) {
    return sentenceCaseSegment(note);
  }
  if (hasCap && note) {
    return `${capacity}-cap ${sentenceCaseSegment(note)}`;
  }
  if (hasCap) {
    return `${capacity}-cap`;
  }
  if (note) {
    return sentenceCaseSegment(note);
  }
  return null;
}

/**
 * Render the venue name with a skyline-glow accent on the middle token when
 * there are 3+ space-separated tokens. Otherwise renders plain.
 *
 * Example: "The Evening Muse" -> "The [Evening] Muse" (Evening accented).
 */
function renderTitle(name: string): React.ReactNode {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 3) {
    return name;
  }
  const middleIndex = Math.floor(parts.length / 2);
  const before = parts.slice(0, middleIndex).join(' ');
  const middle = parts[middleIndex];
  const after = parts.slice(middleIndex + 1).join(' ');

  return (
    <>
      {before}{' '}
      <em className="not-italic text-clt-skyline-glow" data-testid="venue-hero-title-accent">
        {middle}
      </em>{' '}
      {after}
    </>
  );
}

function firstSentence(blurb: string): string {
  const trimmed = blurb.trim();
  const match = /^[\s\S]*?[.!?](?=\s|$)/.exec(trimmed);
  return match ? match[0].trim() : trimmed;
}

const HERO_GRADIENT_CLASS =
  'bg-[radial-gradient(ellipse_50%_60%_at_100%_100%,rgba(232,131,58,0.25)_0%,transparent_60%),radial-gradient(ellipse_60%_80%_at_0%_0%,rgba(14,123,123,0.40)_0%,transparent_60%),linear-gradient(180deg,var(--loci-venue-hero-ground)_0%,var(--loci-color-background)_100%)]';

export function VenueHeroView({
  name,
  neighborhoodName,
  foundedYear,
  capacity,
  capacityNote,
  editorialBlurb,
  postalCode,
  kind,
  coordinates,
  coverUrl,
  coverAlt,
  className,
}: VenueHeroViewProps): React.ReactElement {
  const headingId = React.useId();
  const eyebrow = composeEyebrow({ neighborhoodName, foundedYear, capacity, capacityNote });
  const sub = editorialBlurb ? firstSentence(editorialBlurb) : null;

  return (
    <section
      aria-labelledby={headingId}
      data-testid="venue-hero"
      className={cn(
        '@container relative w-full overflow-hidden pt-12 pb-10',
        HERO_GRADIENT_CLASS,
        className,
      )}
    >
      <div
        data-testid="venue-hero-inner"
        className={cn(
          'mx-auto grid w-full max-w-[1280px] gap-10 px-5 md:px-8',
          'grid-cols-1 items-end',
          // 800px is the hero BOX, not the window. Below this the cover stacks
          // under the title instead of sharing a row the title cannot fit.
          '@min-[800px]:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]',
        )}
      >
        <div className="min-w-0">
          <p
            data-testid="venue-hero-eyebrow"
            className="m-0 mb-4 font-mono text-micro tracking-[0.16em] text-clt-skyline-glow"
          >
            {eyebrow}
          </p>
          <h1
            id={headingId}
            data-testid="venue-hero-heading"
            className="m-0 min-w-0 text-[clamp(56px,7vw,96px)] leading-[0.95] tracking-[-0.025em] text-loci-text [overflow-wrap:anywhere] font-display font-bold"
          >
            {renderTitle(name)}
          </h1>
          {sub && (
            <p
              data-testid="venue-hero-sub"
              className="mt-4 max-w-[50ch] font-body text-h5 leading-[1.5] text-loci-text-muted"
            >
              {sub}
            </p>
          )}
        </div>
        <VenueCoverPanel
          className="min-w-0"
          neighborhoodName={neighborhoodName}
          postalCode={postalCode}
          kind={kind}
          foundedYear={foundedYear}
          coordinates={coordinates}
          coverUrl={coverUrl}
          coverAlt={coverAlt}
        />
      </div>
    </section>
  );
}

/**
 * Connected wrapper for `VenueHeroView`. Currently a thin pass-through; data
 * flows in from the page-level fetcher (`fetchVenueDetail`, WEB-159).
 */
export function VenueHero(props: VenueHeroProps): React.ReactElement {
  return <VenueHeroView {...props} />;
}
